import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Building,
    ExternalLink,
    Search,
    Filter,
    Github,
    Database,
    BarChart3,
    Settings,
    Zap,
    Monitor,
} from 'lucide-react';
import { fetchAppDockEntries } from '../data/adapters';
import { AppDockEntry } from '../types';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const AppDockTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [appEntries, setAppEntries] = useState<AppDockEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const appData = await fetchAppDockEntries(isDemo).catch(() => []);
                setAppEntries(appData);
                if (appData.length === 0) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const getCategoryIcon = (category: string) => {
        switch (category.toLowerCase()) {
            case 'development':
                return <Github size={20} />;
            case 'monitoring':
                return <Monitor size={20} />;
            case 'infrastructure':
                return <Database size={20} />;
            case 'data engineering':
                return <BarChart3 size={20} />;
            case 'project management':
                return <Settings size={20} />;
            default:
                return <Building size={20} />;
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category.toLowerCase()) {
            case 'development':
                return 'text-blue-400';
            case 'monitoring':
                return 'text-green-400';
            case 'infrastructure':
                return 'text-purple-400';
            case 'data engineering':
                return 'text-orange-400';
            case 'project management':
                return 'text-cyan-400';
            default:
                return 'text-slate-400';
        }
    };

    const filteredApps = appEntries.filter(app => {
        const matchesSearch =
            app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const categories = ['all', ...Array.from(new Set(appEntries.map(app => app.category)))];

    if (loading) {
        return (
            <div className="space-y-6">
                {/* App Dock Header */}
                <div
                    className="h-16 bg-slate-800/50 rounded-lg animate-pulse"
                    data-testid="skeleton-placeholder"
                />
                {/* App Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-48 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    if (noData) {
        return (
            <div className="space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                                size={20}
                            />
                            <input
                                type="text"
                                placeholder="Search applications..."
                                disabled
                                className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-slate-500 placeholder-slate-500 cursor-not-allowed"
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Filter size={20} className="text-slate-400" />
                            <select
                                disabled
                                className="px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-slate-500 cursor-not-allowed"
                            >
                                <option>All Categories</option>
                            </select>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-center py-12"
                >
                    <Database size={48} className="text-slate-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">
                        No Applications Available
                    </h3>
                    <p className="text-slate-400">Application data is not available in live mode</p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Quick Access</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                            <div className="flex items-center space-x-3 mb-2">
                                <Github
                                    size={20}
                                    className="text-blue-400 group-hover:text-blue-300"
                                />
                                <span className="text-white font-medium">GitHub</span>
                            </div>
                            <p className="text-sm text-slate-400">Source code & PRs</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                            <div className="flex items-center space-x-3 mb-2">
                                <Monitor
                                    size={20}
                                    className="text-green-400 group-hover:text-green-300"
                                />
                                <span className="text-white font-medium">Grafana</span>
                            </div>
                            <p className="text-sm text-slate-400">Metrics & dashboards</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                            <div className="flex items-center space-x-3 mb-2">
                                <Database
                                    size={20}
                                    className="text-purple-400 group-hover:text-purple-300"
                                />
                                <span className="text-white font-medium">RabbitMQ</span>
                            </div>
                            <p className="text-sm text-slate-400">Queue management</p>
                        </button>

                        <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                            <div className="flex items-center space-x-3 mb-2">
                                <Zap
                                    size={20}
                                    className="text-orange-400 group-hover:text-orange-300"
                                />
                                <span className="text-white font-medium">Airflow</span>
                            </div>
                            <p className="text-sm text-slate-400">Workflow orchestration</p>
                        </button>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Search and Filter */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="evolut-metric-card p-6"
            >
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                            size={20}
                        />
                        <input
                            type="text"
                            placeholder="Search applications..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="flex items-center space-x-2">
                        <Filter size={20} className="text-slate-400" />
                        <select
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                            className="px-4 py-2 bg-slate-800/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
                        >
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category === 'all' ? 'All Categories' : category}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </motion.div>

            {/* App Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredApps.map((app, index) => (
                    <motion.div
                        key={app.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="evolut-metric-card p-6 hover:scale-105 transition-transform duration-200 cursor-pointer group"
                        onClick={() => window.open(app.url, '_blank')}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div
                                className={`p-2 rounded-lg bg-slate-800/50 ${getCategoryColor(app.category)}`}
                            >
                                {getCategoryIcon(app.category)}
                            </div>
                            <ExternalLink
                                size={16}
                                className="text-slate-400 group-hover:text-blue-400 transition-colors"
                            />
                        </div>

                        <h3 className="text-lg font-semibold text-white mb-2">{app.name}</h3>
                        <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                            {app.description}
                        </p>

                        <div className="flex items-center justify-between">
                            <span
                                className={`px-2 py-1 rounded-full text-xs font-medium bg-slate-700/50 text-slate-300`}
                            >
                                {app.category}
                            </span>
                            <div className="text-xs text-slate-500">
                                {app.permissions.join(', ')}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Quick Access Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Quick Access</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                        <div className="flex items-center space-x-3 mb-2">
                            <Github size={20} className="text-blue-400 group-hover:text-blue-300" />
                            <span className="text-white font-medium">GitHub</span>
                        </div>
                        <p className="text-sm text-slate-400">Source code & PRs</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                        <div className="flex items-center space-x-3 mb-2">
                            <Monitor
                                size={20}
                                className="text-green-400 group-hover:text-green-300"
                            />
                            <span className="text-white font-medium">Grafana</span>
                        </div>
                        <p className="text-sm text-slate-400">Metrics & dashboards</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                        <div className="flex items-center space-x-3 mb-2">
                            <Database
                                size={20}
                                className="text-purple-400 group-hover:text-purple-300"
                            />
                            <span className="text-white font-medium">RabbitMQ</span>
                        </div>
                        <p className="text-sm text-slate-400">Queue management</p>
                    </button>

                    <button className="p-4 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700/50 transition-colors text-left group">
                        <div className="flex items-center space-x-3 mb-2">
                            <Zap
                                size={20}
                                className="text-orange-400 group-hover:text-orange-300"
                            />
                            <span className="text-white font-medium">Airflow</span>
                        </div>
                        <p className="text-sm text-slate-400">Workflow orchestration</p>
                    </button>
                </div>
            </motion.div>

            {/* Category Summary */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Application Categories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories
                        .filter(cat => cat !== 'all')
                        .map(category => {
                            const categoryApps = appEntries.filter(
                                app => app.category === category
                            );
                            return (
                                <div
                                    key={category}
                                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                                >
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div
                                            className={`p-2 rounded-lg bg-slate-700/50 ${getCategoryColor(category)}`}
                                        >
                                            {getCategoryIcon(category)}
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-white">{category}</h4>
                                            <p className="text-sm text-slate-400">
                                                {categoryApps.length} applications
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        {categoryApps.map(app => app.name).join(', ')}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            </motion.div>

            {/* No Results */}
            {filteredApps.length === 0 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="text-center py-12"
                >
                    <Building size={48} className="text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">No applications found</h3>
                    <p className="text-slate-400 mb-4">
                        Try adjusting your search terms or category filter
                    </p>
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setSelectedCategory('all');
                        }}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                    >
                        Clear Filters
                    </button>
                </motion.div>
            )}
        </div>
    );
};
